type Activity = {
  delay: number;
  lastRunDate: number;
  fnc?: () => void;
};

const activities: Record<string, Activity> = {};

let timerId: ReturnType<typeof setInterval> | undefined;

const hasPendingActivity = () => Object.values(activities).some((a) => a.fnc);

const startTimerIfNeeded = () => {
  if (timerId) return;
  const delays = Object.values(activities).map((a) => a.delay);
  timerId = setInterval(tick, Math.min(...delays));
};

const stopTimerIfNeeded = () => {
  if (!timerId) return;
  if (!hasPendingActivity()) {
    clearInterval(timerId);
    timerId = undefined;
  }
};

const tick = () => {
  const now = Date.now();
  for (const activity of Object.values(activities)) {
    if (!activity.fnc) continue;
    if (now - activity.lastRunDate >= activity.delay) {
      runActivity(activity, now, false);
    }
  }
  stopTimerIfNeeded();
};

const runActivity = (activity: Activity, now: number, checkTimer: boolean) => {
  activity.fnc?.();
  activity.lastRunDate = now;
  activity.fnc = undefined;
  if (checkTimer) stopTimerIfNeeded();
};

/**
 * @param options.policy 'drop' - fires immediately if interval has elapsed, otherwise discards the call entirely.
 * @param options.policy 'replace' - fires immediately if interval has elapsed, otherwise schedules the latest fnc to run once the interval elapses.
 */
const throttle = (key: string, interval: number, options: { policy: 'replace' | 'drop' }, fnc: () => void) => {
  if (!fnc) throw new Error('fnc can not be undefined');
  const { policy } = options;

  const activity = (activities[key] ??= {
    lastRunDate: 0,
    delay: interval,
    fnc,
  });

  const now = Date.now();
  if (now - activity.lastRunDate >= activity.delay) {
    activity.fnc = fnc;
    runActivity(activity, now, true);
    return;
  }

  if (policy === 'replace') {
    activity.fnc = fnc;
    startTimerIfNeeded();
  }
};

export { throttle };
